// Puente de SOLO LECTURA hacia `clients` para datos de referencia de cliente (spec.md §4,
// plan.md §3.3, tasks.md T30). Todo el acceso a red vive en infrastructure/ (regla #3). §7-I:
// `clients` aún no está migrado — puente `zod`-ligero autocontenido, mismo precedente que
// `catalogOptions.repository.ts`/`enterpriseOptions.repository.ts`. Condición de retiro: cuando
// migre `clients`.
//
// `getDevicesByRack` es la **fuente real** del listado de instancias de UC-11
// (`ClientDeviceInstancesPage.jsx:92-97`, endpoint `/clients/:id/racks/:id/devices`) — NO
// `deviceInstance.repository.getAll` (asimetría real de spec.md §3 a preservar, tasks.md T30).

import { z } from 'zod';
import apiClient from '@shared/api/http';
import { toItemArray, toSnakeCaseKeys } from '@shared/api/keys';
import type { DeviceInstanceSummary } from '../domain/deviceInstance';
import type { DeviceId, DeviceInstanceId } from '../domain/ids';

const IdSchema = z.union([z.string(), z.number()]).transform(String);

// Forma mínima (id + nombre + is_group): este puente solo alimenta encabezados/breadcrumbs
// (`client?.name`) y el selector "empresa del grupo" (`client?.is_group`, UC-12/UC-19), no replica
// el DTO completo del futuro contexto `clients` (billing/contactos/datacenters asignados/...).
const ClientReferenceSchema = z.object({
  id: IdSchema,
  name: z.string(),
  is_group: z.boolean().nullish(),
});
export type ClientReference = z.infer<typeof ClientReferenceSchema>;

// Mismo wire-contract que `deviceInstance.repository.ts` (T21) pero solo los campos que
// `ClientDeviceInstancesPage.jsx:215-227,318-407` realmente lee — este puente alimenta un listado,
// no el detalle completo (30 campos) que sí vive en `deviceInstance.repository.ts`.
//
// v1 real (confirmado contra backend, 2026-08-21) anida `deviceModel` como objeto
// (`{id,name,manufacturer,categoryName}`), no como string plano, y usa `serialNumber`/
// `operationalStatus` en vez de `serial`/`status` — el shape flat legado que tenía este DTO nunca
// hizo match y hacía fallar el `.parse()` silenciosamente (catch de `useDeviceInstancesList`,
// vaciando el listado sin importar lo que devolviera el endpoint).
const DeviceModelRefSchema = z.object({
  id: z.string().nullish(),
  name: z.string().nullish(),
  manufacturer: z.string().nullish(),
  model_identifier: z.string().nullish(),
  category_name: z.string().nullish(),
});

const NamedRefSchema = z.object({
  id: z.string().nullish(),
  code: z.string().nullish(),
  name: z.string().nullish(),
});

const DeviceInstanceByRackDtoSchema = z.object({
  id: z.string(),
  device_model: DeviceModelRefSchema.nullish(),
  serial_number: z.string().nullish(),
  management_ip: z.string().nullish(),
  operational_status: z.string().nullish(),
  ownership_type: z.enum(['Kyndryl', 'Client']).nullish(),
  owning_client_id: z.string().nullish(),
  owning_enterprise_id: z.string().nullish(),
  company_id: z.string().nullish(),
  client: NamedRefSchema.nullish(),
  rack: NamedRefSchema.nullish(),
  rack_units: z.array(z.number()).optional(),
});

function toDeviceInstance(
  dto: z.infer<typeof DeviceInstanceByRackDtoSchema>
): DeviceInstanceSummary {
  const deviceModelRef = {
    id: dto.device_model?.id ? (dto.device_model.id as DeviceId) : undefined,
    name: dto.device_model?.name ?? undefined,
    manufacturer: dto.device_model?.manufacturer ?? undefined,
    modelIdentifier: dto.device_model?.model_identifier ?? undefined,
    categoryName: dto.device_model?.category_name ?? undefined,
  };
  const client = {
    id: dto.client?.id ?? undefined,
    name: dto.client?.name ?? undefined,
  };
  const rack = {
    id: dto.rack?.id ?? undefined,
    code: dto.rack?.code ?? undefined,
    name: dto.rack?.name ?? undefined,
  };

  return {
    id: dto.id as DeviceInstanceId,
    deviceId: (dto.device_model?.id ?? '') as DeviceId,
    deviceModel: dto.device_model?.name ?? undefined,
    deviceBrand: dto.device_model?.manufacturer ?? undefined,
    deviceType: dto.device_model?.category_name ?? undefined,
    model: dto.device_model?.model_identifier ?? undefined,
    serial: dto.serial_number ?? undefined,
    managementIp: dto.management_ip ?? undefined,
    status: dto.operational_status ?? undefined,
    ownerType: dto.ownership_type ?? undefined,
    ownerClientId: dto.owning_client_id ?? undefined,
    ownerEnterpriseId: dto.owning_enterprise_id ?? undefined,
    clientId: dto.client?.id ?? undefined,
    clientName: dto.client?.name ?? undefined,
    clientCompanyId: dto.company_id ?? undefined,
    rackId: dto.rack?.id ?? undefined,
    rackName: dto.rack?.name ?? undefined,
    rackUnits: dto.rack_units,
    deviceModelRef,
    client,
    rack,
  };
}

export const clientReferenceRepository = {
  async getById(id: string): Promise<ClientReference | null> {
    const { data } = await apiClient.get(`/clients/${id}`);
    if (!data) return null;
    return ClientReferenceSchema.parse(toSnakeCaseKeys(data));
  },

  async getAll(): Promise<ClientReference[]> {
    const { data } = await apiClient.get('/clients');
    return z.array(ClientReferenceSchema).parse(toSnakeCaseKeys(toItemArray(data)));
  },

  // UC-11: v1 cuelga las instancias del rack, sin el cliente en la ruta.
  async getDevicesByRack(_clientId: string, rackId: string): Promise<DeviceInstanceSummary[]> {
    const { data } = await apiClient.get(`/racks/${rackId}/device-instances`);
    return z
      .array(DeviceInstanceByRackDtoSchema)
      .parse(toSnakeCaseKeys(toItemArray(data)))
      .map(toDeviceInstance);
  },
};
